import React, { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import axios from "axios";
import { axiosClient } from "@/lib/axiosClient";
import {
    Search, Eye, CheckCircle2, XCircle, Clock,
    Loader2, FileText, User, Phone, BookOpen,
    Building2, Edit3, ShieldCheck, X, Upload,
    Users, FileWarning, CheckSquare, ClipboardCheck, Layers,
    Receipt, Send, CreditCard, Mic, Wallet, CircleDollarSign,
    Download, ChevronDown, ChevronRight, // ✅ Tambahan icon Chevron untuk Expandable Row
    RefreshCw
} from "lucide-react";
import BypassRegistrationModal from "@/components/private/conference-verification/BypassRegistrationModal";
import { useUser } from "@/context/useUser";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import toast from "react-hot-toast";
import { cn } from "@/lib/utils";
import { format } from "date-fns";
import { id } from "date-fns/locale";

// --- INTERFACES SESUAI RESPONSE API BARU ---
interface SummaryData {
    total: number;
    verification: {
        pending: number;
        approved: number;
        rejected: number;
        not_required: number;
    };
    payment: {
        unpaid: number;
        paid: number;
        expired: number;
        refunded: number;
        total_income_idr: number; // ✅ Ditambahkan
        total_income_usd: number; // ✅ Ditambahkan
    };
}

interface RegistrationDetail {
    registration_id: number;
    invoice_number: string;
    participant_name: string;
    email: string;
    whatsapp_number: string;
    nim: string | null;
    nik: string | null;
    institution_name: string | null;
    submission_title: string | null;
    submission_authors: string | null;
    is_presenter: boolean;
    participant_type: string;
    verification_status: "pending" | "approved" | "rejected" | "not_required";
    reason: string | null;
    ktm_path: string | null;
    amount: string;
    currency: string;
    status: "unpaid" | "paid" | "expired" | "refunded";
    public_token: string;
    billing_url: string;
    invitation_url: string | null;
    inv_sent_at: string | null;
    conference: {
        conference_id: number;
        name: string;
    };
    package: {
        package_id: number;
        name: string;
        participant_type_target: string;
    };
}

interface PaginatedData {
    current_page: number;
    last_page: number;
    total: number;
    per_page: number;
    from: number | null;
    to: number | null;
    data: RegistrationDetail[];
}

interface VerificationAPIResponse {
    status: string;
    summary: SummaryData;
    data: PaginatedData;
}

// Interface untuk Dropdown Conference
interface ConferenceOption {
    conference_id: number;
    name: string;
    slug: string;
}

// Error Handler untuk Axios
interface LaravelErrorResponse {
    status?: string;
    message: string;
    errors?: Record<string, string[]>;
}

const parseLaravelError = (error: unknown): string => {
    if (axios.isAxiosError<LaravelErrorResponse>(error)) {
        const data = error.response?.data;
        if (data?.errors) {
            const firstErrorKey = Object.keys(data.errors)[0];
            return data.errors[firstErrorKey][0];
        }
        if (data?.message) {
            return data.message;
        }
        return "Terjadi kesalahan sistem. Silakan coba lagi.";
    }
    if (error instanceof Error) return error.message;
    return "Terjadi kesalahan yang tidak diketahui.";
};

export default function ConferenceVerificationPage() {
    const queryClient = useQueryClient();

    // --- STATE MANAGEMENT ---
    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(15);
    const [statusFilter, setStatusFilter] = useState<string>("all");
    const [conferenceFilter, setConferenceFilter] = useState<string>("all");
    const [presenterFilter, setPresenterFilter] = useState<string>("all");
    const [paymentStatusFilter, setPaymentStatusFilter] = useState<string>("all");
    const [searchQuery, setSearchQuery] = useState("");

    // State untuk Modal Detail
    const [selectedId, setSelectedId] = useState<number | null>(null);
    const [activeTab, setActiveTab] = useState<"verify" | "edit">("verify");

    // ✅ State untuk baris yang di-expand (Accordion)
    const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());

    // State untuk Modal Export Excel
    const [isExportModalOpen, setIsExportModalOpen] = useState<boolean>(false);
    const [isBypassModalOpen, setIsBypassModalOpen] = useState<boolean>(false);
    const [exportConferenceId, setExportConferenceId] = useState<string>("all");
    const [exportPresenter, setExportPresenter] = useState<string>("all");
    const [exportPaymentStatus, setExportPaymentStatus] = useState<string>("all");
    const [exportVerificationStatus, setExportVerificationStatus] = useState<string>("all");
    const [isExporting, setIsExporting] = useState<boolean>(false);

    // --- FETCH DATA CONFERENCES (UNTUK DROPDOWN) ---
    const { data: conferencesData } = useQuery({
        queryKey: ["conferences-dropdown"],
        queryFn: async (): Promise<ConferenceOption[]> => {
            const response = await axiosClient.get(`/private/conference-billing/accessible-conferences`);
            return response.data.data;
        }
    });

    // --- FETCH DATA LIST ---
    const { data: apiResponse, isLoading: listLoading, isFetching } = useQuery({
        queryKey: ["conference-verifications", page, perPage, statusFilter, conferenceFilter, presenterFilter, paymentStatusFilter, searchQuery],
        queryFn: async (): Promise<VerificationAPIResponse> => {
            let url = `/private/conference-billing?page=${page}&per_page=${perPage}`;
            if (statusFilter !== "all") url += `&verification_status=${statusFilter}`;
            if (conferenceFilter !== "all") url += `&conference_id=${conferenceFilter}`;
            if (presenterFilter !== "all") url += `&is_presenter=${presenterFilter}`;
            if (paymentStatusFilter !== "all") url += `&status=${paymentStatusFilter}`;
            if (searchQuery) url += `&search=${searchQuery}`;

            const response = await axiosClient.get(url);
            return response.data;
        },
        placeholderData: keepPreviousData,
    });

    const listData = apiResponse?.data;
    const summary = apiResponse?.summary;

    // --- FETCH DATA DETAIL ---
    const { data: detailData, isLoading: detailLoading } = useQuery({
        queryKey: ["conference-billing-detail", selectedId],
        queryFn: async (): Promise<RegistrationDetail | null> => {
            if (!selectedId) return null;
            const response = await axiosClient.get(`/private/conference-billing/${selectedId}`);
            return response.data.data;
        },
        enabled: !!selectedId,
    });

    // --- MUTASI VERIFIKASI ---
    const [verifyForm, setVerifyForm] = useState({ status: "approved", reason: "" });

    useEffect(() => {
        if (detailData) {
            setVerifyForm({
                status: detailData.verification_status === "pending" ? "approved" : detailData.verification_status,
                reason: detailData.reason || ""
            });
        }
    }, [detailData]);

    const verifyMutation = useMutation({
        mutationFn: async () => {
            const response = await axiosClient.patch(`/private/conference-billing/${selectedId}/verify`, verifyForm);
            return response.data;
        },
        onSuccess: (data) => {
            if (data.status === "error") {
                toast.error(data.message || "Gagal memverifikasi data.");
                return;
            }
            toast.success(data.message || "Verifikasi berhasil disimpan.");
            queryClient.invalidateQueries({ queryKey: ["conference-verifications"] });
            queryClient.invalidateQueries({ queryKey: ["conference-billing-detail", selectedId] });
            setSelectedId(null);
        },
        onError: (error) => toast.error(parseLaravelError(error)),
    });

    // --- MUTASI SYNC ULANG PAPER DARI OJS ---
    const syncPaperMutation = useMutation({
        mutationFn: async () => {
            const response = await axiosClient.post(`/private/conference-billing/${selectedId}/sync-paper`);
            return response.data;
        },
        onSuccess: (data) => {
            if (data.status === "error") {
                toast.error(data.message || "Gagal sinkronisasi ulang naskah.");
                return;
            }
            if (data.data?.title_changed || data.data?.authors_changed) {
                toast.success("Judul/penulis naskah berhasil diperbarui dari OJS.");
            } else {
                toast.success("Data naskah sudah sesuai dengan OJS (tidak ada perubahan).");
            }
            queryClient.invalidateQueries({ queryKey: ["conference-billing-detail", selectedId] });
            queryClient.invalidateQueries({ queryKey: ["conference-verifications"] });
        },
        onError: (error) => toast.error(parseLaravelError(error)),
    });

    // --- MUTASI UPDATE DATA ---
    const { data: currentUser } = useUser();
    const isSuperAdmin = currentUser?.role_id === 1;

    const [editForm, setEditForm] = useState({
        participant_name: "", email: "", whatsapp_number: "", nim: "", ktm_file: null as File | null
    });
    const [overrideReason, setOverrideReason] = useState("");
    const [showOverrideField, setShowOverrideField] = useState(false);

    useEffect(() => {
        if (detailData) {
            setEditForm({
                participant_name: detailData.participant_name || "",
                email: detailData.email || "",
                whatsapp_number: detailData.whatsapp_number || "",
                nim: detailData.nim || detailData.nik || "",
                ktm_file: null
            });
            setOverrideReason("");
            setShowOverrideField(false);
        }
    }, [detailData]);

    const updateMutation = useMutation({
        mutationFn: async () => {
            const formData = new FormData();
            formData.append("participant_name", editForm.participant_name);
            formData.append("email", editForm.email);
            formData.append("whatsapp_number", editForm.whatsapp_number);
            formData.append("nim", editForm.nim);
            if (editForm.ktm_file) formData.append("ktm_file", editForm.ktm_file);
            if (showOverrideField && overrideReason.trim()) formData.append("override_reason", overrideReason.trim());

            const response = await axiosClient.post(`/private/conference-billing/${selectedId}/update`, formData, {
                headers: { "Content-Type": "multipart/form-data" }
            });
            return response.data;
        },
        onSuccess: (data) => {
            if (data.status === "error") {
                toast.error(data.message || "Gagal memperbarui data.");
                return;
            }
            toast.success(data.message || "Data berhasil diperbarui.");
            setShowOverrideField(false);
            setOverrideReason("");
            queryClient.invalidateQueries({ queryKey: ["conference-verifications"] });
            queryClient.invalidateQueries({ queryKey: ["conference-billing-detail", selectedId] });
            setActiveTab("verify");
        },
        onError: (error) => {
            const message = parseLaravelError(error);
            toast.error(message);
            // Kalau ini Super Admin dan gagal karena kunci VA sudah terbit, tawarkan jalur override
            // (isi alasan koreksi) alih-alih cuma menampilkan pesan gagal begitu saja.
            if (isSuperAdmin && message.includes("tagihan Virtual Account sudah terbit")) {
                setShowOverrideField(true);
            }
        },
    });

    // --- MUTASI KIRIM INVITATION ---
    const sendInvitationMutation = useMutation({
        mutationFn: async () => {
            const response = await axiosClient.post(`/private/conference-billing/${selectedId}/send-invitation`);
            return response.data;
        },
        onSuccess: (data) => {
            if (data.status === "error") {
                toast.error(data.message || "Gagal mengirim Invitation Letter.");
                return;
            }
            toast.success(data.message || "Invitation Letter berhasil dikirim.");
            queryClient.invalidateQueries({ queryKey: ["conference-billing-detail", selectedId] });
        },
        onError: (error) => toast.error(parseLaravelError(error)),
    });

    // MUTASI KIRIM ULANG EMAIL PEMBAYARAN (EXPIRED/PAID)
    const resendPaymentEmailMutation = useMutation({
        mutationFn: async () => {
            const response = await axiosClient.post(`/private/conference-billing/${selectedId}/resend-email`);
            return response.data;
        },
        onSuccess: (data) => {
            if (data.status === "error") {
                toast.error(data.message || "Gagal mengirim ulang email pembayaran.");
                return;
            }
            toast.success(data.message || "Email berhasil dikirim ulang ke peserta.");
        },
        onError: (error) => toast.error(parseLaravelError(error)),
    });

    // Fungsi Handler Modal Export Excel Terintegrasi Parameter
    const handleExportExcel = async () => {
        setIsExporting(true);
        try {
            const params = new URLSearchParams();
            if (exportConferenceId && exportConferenceId !== "all") params.append("conference_id", exportConferenceId);
            if (exportPresenter && exportPresenter !== "all") params.append("is_presenter", exportPresenter);
            if (exportPaymentStatus && exportPaymentStatus !== "all") params.append("payment_status", exportPaymentStatus);
            if (exportVerificationStatus && exportVerificationStatus !== "all") params.append("verification_status", exportVerificationStatus);
            if (searchQuery) params.append("search", searchQuery);

            const response = await axiosClient.get(`/private/conference-billing/export?${params.toString()}`, {
                responseType: 'blob',
            });

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            
            let fileName = 'Conference_Billing_Export.xlsx';
            const disposition = response.headers['content-disposition'];
            if (disposition && disposition.indexOf('attachment') !== -1) {
                const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
                const matches = filenameRegex.exec(disposition);
                if (matches != null && matches[1]) { 
                    fileName = matches[1].replace(/['"]/g, '');
                }
            }
            
            link.setAttribute('download', fileName);
            document.body.appendChild(link);
            link.click();
            link.remove();
            
            toast.success("Berhasil mengekspor data registrasi.");
            setIsExportModalOpen(false);
        } catch (error) {
            console.error("Export error:", error);
            toast.error("Gagal mengekspor data ke Excel.");
        } finally {
            setIsExporting(false);
        }
    };

    // ✅ Fungsi Handler Expand Row
    const toggleRow = (id: number) => {
        setExpandedRows(prev => {
            const newSet = new Set(prev);
            if (newSet.has(id)) {
                newSet.delete(id);
            } else {
                newSet.add(id);
            }
            return newSet;
        });
    };

    // --- HELPER RENDER ---
    const formatCurrency = (amount: string | number, currency: string) => {
        return new Intl.NumberFormat(currency === "USD" ? "en-US" : "id-ID", {
            style: "currency",
            currency: currency,
            minimumFractionDigits: 0,
        }).format(Number(amount));
    };

    const formatDate = (dateString: string | null) => {
        if (!dateString) return "-";
        try {
            return format(new Date(dateString), "dd MMM yyyy, HH:mm", { locale: id });
        } catch {
            return dateString;
        }
    };

    const getStatusBadge = (status: string) => {
        switch (status) {
            case "approved": return <span className="px-2.5 py-1 bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400 rounded-md text-xs font-bold uppercase flex items-center gap-1 w-fit"><CheckCircle2 className="w-3.5 h-3.5" /> Approved</span>;
            case "rejected": return <span className="px-2.5 py-1 bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400 rounded-md text-xs font-bold uppercase flex items-center gap-1 w-fit"><XCircle className="w-3.5 h-3.5" /> Rejected</span>;
            case "not_required": return <span className="px-2.5 py-1 bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-400 rounded-md text-xs font-bold uppercase flex items-center gap-1 w-fit"><ShieldCheck className="w-3.5 h-3.5" /> Auto-Verify</span>;
            default: return <span className="px-2.5 py-1 bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 rounded-md text-xs font-bold uppercase flex items-center gap-1 w-fit"><Clock className="w-3.5 h-3.5" /> Pending</span>;
        }
    };

    const getPaymentStatusBadge = (status: string) => {
        switch (status) {
            case "paid": return <span className="text-emerald-600 dark:text-emerald-400 text-xs font-bold uppercase flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" /> Lunas (Paid)</span>;
            case "expired": return <span className="text-rose-600 dark:text-rose-400 text-xs font-bold uppercase flex items-center gap-1"><XCircle className="w-3.5 h-3.5" /> Kedaluwarsa</span>;
            case "refunded": return <span className="text-slate-500 dark:text-slate-400 text-xs font-bold uppercase flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> Dikembalikan</span>;
            default: return <span className="text-amber-600 dark:text-amber-400 text-xs font-bold uppercase flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> Belum Dibayar</span>;
        }
    };

    return (
        <div className="max-w-7xl mx-auto space-y-8 pb-20 px-4 md:px-0">

            {/* --- HEADER --- */}
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 bg-white dark:bg-slate-900 p-6 md:p-8 rounded-3xl shadow-sm border border-slate-100 dark:border-slate-800">
                <div>
                    <div className="flex items-center gap-3 mb-2">
                        <div className="p-2.5 rounded-xl bg-blue-50 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400">
                            <ClipboardCheck className="w-6 h-6" />
                        </div>
                        <h1 className="text-2xl font-black text-slate-900 dark:text-white tracking-tight">Verifikasi Konferensi</h1>
                    </div>
                    <p className="text-sm text-slate-500 dark:text-slate-400 max-w-xl">
                        Tinjau dan verifikasi dokumen identitas peserta agar mereka dapat melanjutkan ke tahap pembayaran.
                    </p>
                </div>

                <div className="flex flex-col md:flex-row items-center gap-3">
                    <Button 
                        onClick={() => setIsBypassModalOpen(true)}
                        className="bg-blue-600 hover:bg-blue-700 text-white rounded-xl h-11 px-6 font-bold shadow-md shrink-0 transition-all flex items-center gap-2"
                    >
                        <ShieldCheck className="w-4 h-4" />
                        Bypass Pendaftaran
                    </Button>
                    <Button 
                        onClick={() => {
                            setExportConferenceId(conferenceFilter);
                            setExportPresenter(presenterFilter);
                            setExportPaymentStatus(paymentStatusFilter);
                            setExportVerificationStatus(statusFilter);
                            setIsExportModalOpen(true);
                        }}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl h-11 px-6 font-bold shadow-md shrink-0 transition-all flex items-center gap-2"
                    >
                        <Download className="w-4 h-4" />
                        Export Excel
                    </Button>
                </div>
            </div>

            {/* --- SUMMARY CARDS (SKELETON & ACTUAL) --- */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {isFetching && !summary ? (
                    Array.from({ length: 8 }).map((_, i) => (
                        <div key={i} className="bg-white dark:bg-slate-900 p-5 rounded-2xl border border-slate-100 dark:border-slate-800 flex items-center gap-4 shadow-sm h-[88px]">
                            <Skeleton className="w-12 h-12 rounded-xl shrink-0 dark:bg-slate-800" />
                            <div className="space-y-2.5 w-full">
                                <Skeleton className="h-3 w-24 dark:bg-slate-800" />
                                <Skeleton className="h-6 w-12 dark:bg-slate-800" />
                            </div>
                        </div>
                    ))
                ) : summary ? (
                    <>
                        {/* BARIS 1: Verifikasi */}
                        <div className="bg-white dark:bg-slate-900 p-5 rounded-2xl border border-slate-100 dark:border-slate-800 flex items-center gap-4 shadow-sm h-[88px] animate-in fade-in duration-300">
                            <div className="p-3 bg-blue-50 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400 rounded-xl"><Users className="w-6 h-6" /></div>
                            <div>
                                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">Total Pendaftar</p>
                                <p className="text-2xl font-black text-slate-900 dark:text-white leading-none">{summary.total}</p>
                            </div>
                        </div>
                        <div className="bg-white dark:bg-slate-900 p-5 rounded-2xl border border-slate-100 dark:border-slate-800 flex items-center gap-4 shadow-sm h-[88px] animate-in fade-in duration-300">
                            <div className="p-3 bg-amber-50 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400 rounded-xl"><Clock className="w-6 h-6" /></div>
                            <div>
                                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">Menunggu Validasi</p>
                                <p className="text-2xl font-black text-slate-900 dark:text-white leading-none">{summary.verification.pending}</p>
                            </div>
                        </div>
                        <div className="bg-white dark:bg-slate-900 p-5 rounded-2xl border border-slate-100 dark:border-slate-800 flex items-center gap-4 shadow-sm h-[88px] animate-in fade-in duration-300">
                            <div className="p-3 bg-emerald-50 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400 rounded-xl"><CheckSquare className="w-6 h-6" /></div>
                            <div>
                                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">Telah Disetujui</p>
                                <p className="text-2xl font-black text-slate-900 dark:text-white leading-none">{summary.verification.approved}</p>
                            </div>
                        </div>
                        <div className="bg-white dark:bg-slate-900 p-5 rounded-2xl border border-slate-100 dark:border-slate-800 flex items-center gap-4 shadow-sm h-[88px] animate-in fade-in duration-300">
                            <div className="p-3 bg-rose-50 text-rose-600 dark:bg-rose-900/30 dark:text-rose-400 rounded-xl"><FileWarning className="w-6 h-6" /></div>
                            <div>
                                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">Ditolak / Revisi</p>
                                <p className="text-2xl font-black text-slate-900 dark:text-white leading-none">{summary.verification.rejected}</p>
                            </div>
                        </div>

                        {/* BARIS 2: Pembayaran */}
                        <div className="bg-white dark:bg-slate-900 p-5 rounded-2xl border border-slate-100 dark:border-slate-800 flex items-center gap-4 shadow-sm h-[88px] animate-in fade-in duration-300">
                            <div className="p-3 bg-emerald-50 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400 rounded-xl"><Wallet className="w-6 h-6" /></div>
                            <div className="min-w-0">
                                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1 truncate">Pemasukan (IDR)</p>
                                <p className="text-lg sm:text-xl font-black text-slate-900 dark:text-white leading-none truncate tracking-tighter">{formatCurrency(summary.payment.total_income_idr, 'IDR')}</p>
                            </div>
                        </div>
                        <div className="bg-white dark:bg-slate-900 p-5 rounded-2xl border border-slate-100 dark:border-slate-800 flex items-center gap-4 shadow-sm h-[88px] animate-in fade-in duration-300">
                            <div className="p-3 bg-teal-50 text-teal-600 dark:bg-teal-900/30 dark:text-teal-400 rounded-xl"><CircleDollarSign className="w-6 h-6" /></div>
                            <div className="min-w-0">
                                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1 truncate">Pemasukan (USD)</p>
                                <p className="text-lg sm:text-xl font-black text-slate-900 dark:text-white leading-none truncate tracking-tighter">{formatCurrency(summary.payment.total_income_usd, 'USD')}</p>
                            </div>
                        </div>
                        <div className="bg-white dark:bg-slate-900 p-5 rounded-2xl border border-slate-100 dark:border-slate-800 flex items-center gap-4 shadow-sm h-[88px] animate-in fade-in duration-300">
                            <div className="p-3 bg-sky-50 text-sky-600 dark:bg-sky-900/30 dark:text-sky-400 rounded-xl"><CheckCircle2 className="w-6 h-6" /></div>
                            <div>
                                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">Sudah Lunas</p>
                                <p className="text-2xl font-black text-slate-900 dark:text-white">{summary.payment.paid}</p>
                            </div>
                        </div>
                        <div className="bg-white dark:bg-slate-900 p-5 rounded-2xl border border-slate-100 dark:border-slate-800 flex items-center gap-4 shadow-sm h-[88px] animate-in fade-in duration-300">
                            <div className="p-3 bg-amber-50 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400 rounded-xl"><CreditCard className="w-6 h-6" /></div>
                            <div>
                                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">Belum Dibayar</p>
                                <p className="text-2xl font-black text-slate-900 dark:text-white">{summary.payment.unpaid}</p>
                            </div>
                        </div>
                    </>
                ) : null}
            </div>

            {/* --- TOOLBAR (FILTER & SEARCH) --- */}
            <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {/* Filter Conference */}
                    <div className="bg-white dark:bg-slate-900 px-4 py-3 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-800 flex items-center gap-3">
                        <Layers className="w-5 h-5 text-slate-400 shrink-0 hidden sm:block" />
                        <select
                            value={conferenceFilter}
                            onChange={(e) => { setConferenceFilter(e.target.value); setPage(1); }}
                            className="w-full bg-transparent text-sm font-bold text-slate-700 dark:text-slate-300 outline-none cursor-pointer truncate pr-4"
                        >
                            <option value="all">Semua Conference</option>
                            {conferencesData?.map(conf => (
                                <option key={conf.conference_id} value={conf.conference_id.toString()}>
                                    {conf.slug.toUpperCase()} - {conf.name}
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* Filter Role Presenter */}
                    <div className="bg-white dark:bg-slate-900 px-4 py-3 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-800 flex items-center gap-3">
                        <Mic className="w-5 h-5 text-slate-400 shrink-0 hidden sm:block" />
                        <select
                            value={presenterFilter}
                            onChange={(e) => { setPresenterFilter(e.target.value); setPage(1); }}
                            className="w-full bg-transparent text-sm font-bold text-slate-700 dark:text-slate-300 outline-none cursor-pointer truncate pr-4"
                        >
                            <option value="all">Semua Role</option>
                            <option value="1">Presenter (Pemakalah)</option>
                            <option value="0">Non-Presenter (Peserta)</option>
                        </select>
                    </div>

                    {/* Filter Status Pembayaran */}
                    <div className="bg-white dark:bg-slate-900 px-4 py-3 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-800 flex items-center gap-3">
                        <CreditCard className="w-5 h-5 text-slate-400 shrink-0 hidden sm:block" />
                        <select
                            value={paymentStatusFilter}
                            onChange={(e) => { setPaymentStatusFilter(e.target.value); setPage(1); }}
                            className="w-full bg-transparent text-sm font-bold text-slate-700 dark:text-slate-300 outline-none cursor-pointer truncate pr-4"
                        >
                            <option value="all">Semua Pembayaran</option>
                            <option value="paid">Lunas (Paid)</option>
                            <option value="unpaid">Belum Bayar (Unpaid)</option>
                            <option value="expired">Kedaluwarsa (Expired)</option>
                            <option value="refunded">Dikembalikan (Refunded)</option>
                        </select>
                    </div>
                </div>

                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 bg-slate-50 dark:bg-slate-900/50 p-3 rounded-2xl border border-slate-200 dark:border-slate-800">
                    <div className="flex overflow-x-auto gap-1 scrollbar-hide">
                        {[
                            { id: "all", label: "Semua Data" },
                            { id: "pending", label: "Pending" },
                            { id: "approved", label: "Approved" },
                            { id: "rejected", label: "Rejected" },
                            { id: "not_required", label: "Auto-Verify" }
                        ].map((status) => (
                            <button
                                key={status.id}
                                onClick={() => { setStatusFilter(status.id); setPage(1); }}
                                className={cn(
                                    "px-5 py-2.5 text-sm font-bold rounded-xl whitespace-nowrap transition-all",
                                    statusFilter === status.id
                                        ? "bg-white text-blue-700 shadow-sm border border-slate-200 dark:bg-slate-800 dark:text-blue-400 dark:border-slate-700"
                                        : "text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"
                                )}
                            >
                                {status.label}
                            </button>
                        ))}
                    </div>

                    <div className="relative w-full lg:w-80 shrink-0">
                        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <input
                            type="text"
                            placeholder="Cari nama, invoice..."
                            value={searchQuery}
                            onChange={(e) => { setSearchQuery(e.target.value); setPage(1); }}
                            className="w-full pl-11 pr-4 py-2.5 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 outline-none shadow-sm transition-all"
                        />
                    </div>
                </div>
            </div>

            {/* --- DATA TABLE --- */}
            <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-800 overflow-hidden relative">

                {isFetching && !listLoading && (
                    <div className="absolute inset-0 z-10 bg-white/50 dark:bg-slate-900/50 backdrop-blur-[1px] flex items-center justify-center">
                        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
                    </div>
                )}

                <div className="overflow-x-auto">
                    <table className="w-full text-left text-sm whitespace-nowrap">
                        {/* ✅ PERBAIKAN: Kolom Aksi di-Freeze (Sticky) dan Tambah Header Kosong untuk Toggle */}
                        <thead className="bg-slate-50 dark:bg-slate-800/50 text-slate-500 dark:text-slate-400 font-bold uppercase tracking-wider text-[11px]">
                            <tr>
                                <th className="px-4 py-5 border-b border-slate-100 dark:border-slate-800 w-10 text-center"></th>
                                <th className="px-6 py-5 border-b border-slate-100 dark:border-slate-800">Invoice / Konferensi</th>
                                <th className="px-6 py-5 border-b border-slate-100 dark:border-slate-800">Peserta</th>
                                <th className="px-6 py-5 border-b border-slate-100 dark:border-slate-800">Paket & Kategori</th>
                                <th className="px-6 py-5 border-b border-slate-100 dark:border-slate-800">Status (Verif & Bayar)</th>
                                <th className="px-6 py-5 border-b border-slate-100 dark:border-slate-800 text-right sticky right-0 z-10 bg-slate-50 dark:bg-slate-800/90 shadow-[-10px_0_15px_-10px_rgba(0,0,0,0.1)]">Aksi</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                            {listLoading ? (
                                <tr>
                                    <td colSpan={6} className="p-12 text-center text-slate-500"><Loader2 className="w-8 h-8 animate-spin mx-auto mb-3 text-blue-500" /> Memuat data pendaftar...</td>
                                </tr>
                            ) : (!listData || listData.data.length === 0) ? (
                                <tr>
                                    <td colSpan={6} className="p-12 text-center text-slate-500">Tidak ada data yang sesuai dengan kriteria.</td>
                                </tr>
                            ) : (
                                listData.data.map((item) => (
                                    <React.Fragment key={item.registration_id}>
                                        {/* ✅ PERBAIKAN: Baris Utama dengan group class dan Action Column Sticky */}
                                        <tr className="group hover:bg-slate-50/50 dark:hover:bg-slate-800/20 transition-colors bg-white dark:bg-slate-900">
                                            <td className="px-4 py-4 text-center cursor-pointer" onClick={() => toggleRow(item.registration_id)}>
                                                <button className="p-1.5 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-md transition-colors text-slate-400">
                                                    {expandedRows.has(item.registration_id) ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                                                </button>
                                            </td>
                                            <td className="px-6 py-4">
                                                <p className="font-bold text-slate-900 dark:text-white font-mono">{item.invoice_number}</p>
                                                <p className="text-[11px] text-slate-500 truncate max-w-[200px]">{item.conference.name}</p>
                                            </td>
                                            <td className="px-6 py-4">
                                                <p className="font-bold text-slate-900 dark:text-white flex items-center gap-1.5"><User className="w-3.5 h-3.5 text-slate-400" />{item.participant_name}</p>
                                                <p className="text-[11px] text-slate-500">{item.email}</p>
                                            </td>
                                            <td className="px-6 py-4">
                                                <p className="font-bold text-blue-600 dark:text-blue-400 truncate max-w-[220px]">{item.package.name}</p>
                                                <p className="text-[11px] text-slate-500 uppercase tracking-wide flex items-center gap-1.5">
                                                    {item.participant_type?.replace('_', ' ')} <span className="font-bold text-slate-700 dark:text-slate-300 ml-1">&bull; {formatCurrency(item.amount, item.currency)}</span>
                                                </p>
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="flex flex-col gap-2">
                                                    {getStatusBadge(item.verification_status)}
                                                    {getPaymentStatusBadge(item.status)}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 text-right sticky right-0 z-10 bg-white dark:bg-slate-900 group-hover:bg-slate-50/50 dark:group-hover:bg-slate-800/80 shadow-[-10px_0_15px_-10px_rgba(0,0,0,0.1)] transition-colors">
                                                <Button
                                                    size="sm"
                                                    variant="outline"
                                                    onClick={() => {
                                                        setSelectedId(item.registration_id);
                                                        setActiveTab("verify");
                                                        setVerifyForm({
                                                            status: item.verification_status === "pending" ? "approved" : item.verification_status,
                                                            reason: item.reason || ""
                                                        });
                                                    }}
                                                    className="rounded-xl text-blue-600 border-blue-200 hover:bg-blue-50 dark:text-blue-400 dark:border-blue-900/50 dark:hover:bg-blue-900/30"
                                                >
                                                    <Eye className="w-4 h-4 mr-2" /> Review
                                                </Button>
                                            </td>
                                        </tr>

                                        {/* ✅ PERBAIKAN: Baris Ekstra Expandable (Accordion) */}
                                        {expandedRows.has(item.registration_id) && (
                                            <tr className="bg-slate-50/80 dark:bg-slate-800/30">
                                                <td colSpan={6} className="px-8 py-5 border-t border-slate-100 dark:border-slate-800/50 shadow-inner">
                                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 animate-in fade-in slide-in-from-top-2 duration-300">
                                                        {/* Info Akademik */}
                                                        <div className="space-y-3">
                                                            <h5 className="text-[10px] font-bold text-slate-400 uppercase tracking-widest flex items-center gap-1.5"><Building2 className="w-3.5 h-3.5" /> Profil Akademik</h5>
                                                            <div>
                                                                <p className="text-xs text-slate-500">Institusi</p>
                                                                <p className="text-sm font-semibold text-slate-800 dark:text-slate-200">{item.institution_name || "-"}</p>
                                                            </div>
                                                            <div>
                                                                <p className="text-xs text-slate-500">NIM / NIK</p>
                                                                <p className="text-sm font-semibold font-mono text-slate-800 dark:text-slate-200">{item.nim || item.nik || "-"}</p>
                                                            </div>
                                                        </div>

                                                        {/* Info Kontak & Waktu */}
                                                        <div className="space-y-3">
                                                            <h5 className="text-[10px] font-bold text-slate-400 uppercase tracking-widest flex items-center gap-1.5"><Phone className="w-3.5 h-3.5" /> Kontak & Informasi</h5>
                                                            <div>
                                                                <p className="text-xs text-slate-500">No. WhatsApp</p>
                                                                <p className="text-sm font-semibold text-slate-800 dark:text-slate-200">{item.whatsapp_number || "-"}</p>
                                                            </div>
                                                            {item.inv_sent_at && (
                                                                <div>
                                                                    <p className="text-xs text-slate-500">LoA Dikirim Pada</p>
                                                                    <p className="text-sm font-semibold text-slate-800 dark:text-slate-200">{formatDate(item.inv_sent_at)}</p>
                                                                </div>
                                                            )}
                                                        </div>

                                                        {/* Info Naskah atau Penolakan */}
                                                        <div className="space-y-3">
                                                            {item.verification_status === "rejected" && item.reason ? (
                                                                <>
                                                                    <h5 className="text-[10px] font-bold text-rose-500 uppercase tracking-widest flex items-center gap-1.5"><FileWarning className="w-3.5 h-3.5" /> Alasan Penolakan</h5>
                                                                    <div className="p-3 bg-rose-50 dark:bg-rose-900/20 border border-rose-200 dark:border-rose-900/50 rounded-xl">
                                                                        <p className="text-xs text-rose-700 dark:text-rose-400 font-medium leading-relaxed">{item.reason}</p>
                                                                    </div>
                                                                </>
                                                            ) : item.is_presenter && item.submission_title ? (
                                                                <>
                                                                    <h5 className="text-[10px] font-bold text-blue-500 uppercase tracking-widest flex items-center gap-1.5"><BookOpen className="w-3.5 h-3.5" /> Detail Naskah (Paper)</h5>
                                                                    <div>
                                                                        <p className="text-xs text-slate-500">Judul</p>
                                                                        <p className="text-sm font-semibold text-slate-800 dark:text-slate-200 line-clamp-2 leading-snug" title={item.submission_title}>{item.submission_title}</p>
                                                                    </div>
                                                                    <div>
                                                                        <p className="text-xs text-slate-500">Author</p>
                                                                        <p className="text-sm font-semibold text-slate-800 dark:text-slate-200 truncate">{item.submission_authors || "-"}</p>
                                                                    </div>
                                                                </>
                                                            ) : (
                                                                <div className="h-full flex items-center justify-center opacity-50">
                                                                    <p className="text-xs text-slate-500 italic">Peserta Non-Pemakalah</p>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {listData && listData.total > 0 && (
                    <div className="p-4 md:p-5 border-t border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/50 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm">
                        <div className="flex items-center gap-3 text-slate-500 font-medium">
                            <span>Menampilkan {listData.from || 0} - {listData.to || 0} dari {listData.total} data</span>
                            <div className="h-4 w-px bg-slate-300 dark:bg-slate-700 hidden sm:block"></div>
                            <div className="flex items-center gap-2 sm:flex">
                                <label htmlFor="perPage">Tampilkan:</label>
                                <select
                                    id="perPage"
                                    value={perPage}
                                    onChange={(e) => { setPerPage(Number(e.target.value)); setPage(1); }}
                                    className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg px-2 py-1 outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer"
                                >
                                    <option value={15}>15</option>
                                    <option value={25}>25</option>
                                    <option value={50}>50</option>
                                    <option value={100}>100</option>
                                </select>
                            </div>
                        </div>
                        <div className="flex gap-2">
                            <Button variant="outline" size="sm" className="rounded-lg" disabled={page === 1} onClick={() => setPage(p => p - 1)}>Sebelumnya</Button>
                            <Button variant="outline" size="sm" className="rounded-lg" disabled={page === listData.last_page} onClick={() => setPage(p => p + 1)}>Selanjutnya</Button>
                        </div>
                    </div>
                )}
            </div>

            {/* ================================================================================= */}
            {/* MODAL DETAIL & VERIFIKASI */}
            {/* ================================================================================= */}
            {selectedId && (
                <div className="fixed inset-0 z-[999] flex items-center justify-center p-4 md:p-6 bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-200">
                    <div className="bg-white dark:bg-slate-900 rounded-[2rem] w-full max-w-5xl max-h-[95vh] flex flex-col shadow-2xl border border-slate-200 dark:border-slate-700 overflow-hidden animate-in zoom-in-95">

                        {/* Modal Header */}
                        <div className="px-8 py-5 border-b border-slate-100 dark:border-slate-800 flex justify-between items-center bg-slate-50 dark:bg-slate-800/50 shrink-0">
                            <div>
                                <h3 className="font-black text-xl text-slate-900 dark:text-white flex items-center gap-2">
                                    <ClipboardCheck className="w-6 h-6 text-blue-600" /> Detail Pendaftaran
                                </h3>
                                <p className="text-sm text-slate-500 font-mono mt-1">ID Registrasi: {selectedId}</p>
                            </div>
                            <button onClick={() => setSelectedId(null)} className="p-2.5 bg-slate-200/50 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 rounded-full transition-colors">
                                <X className="w-5 h-5 text-slate-500 dark:text-slate-400" />
                            </button>
                        </div>

                        {/* Modal Body */}
                        <div className="flex-1 overflow-y-auto">
                            {detailLoading || !detailData ? (
                                <div className="p-20 flex flex-col items-center text-slate-400">
                                    <Loader2 className="w-10 h-10 animate-spin mb-4 text-blue-600" />
                                    <p>Mengambil data detail...</p>
                                </div>
                            ) : (
                                <div className="min-h-full flex flex-col">
                                    {/* TABS NAV */}
                                    <div className="flex border-b border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/50 px-8 pt-2 shrink-0">
                                        <button
                                            onClick={() => setActiveTab("verify")}
                                            className={cn("px-5 py-4 text-sm font-bold border-b-2 transition-colors flex items-center gap-2", activeTab === "verify" ? "border-blue-600 text-blue-600 dark:text-blue-400" : "border-transparent text-slate-500 hover:text-slate-700")}
                                        >
                                            <ShieldCheck className="w-4 h-4" /> Review & Verifikasi
                                        </button>
                                        <button
                                            onClick={() => setActiveTab("edit")}
                                            className={cn("px-5 py-4 text-sm font-bold border-b-2 transition-colors flex items-center gap-2", activeTab === "edit" ? "border-amber-500 text-amber-600 dark:text-amber-400" : "border-transparent text-slate-500 hover:text-slate-700")}
                                        >
                                            <Edit3 className="w-4 h-4" /> Edit Data Pendaftar
                                        </button>
                                    </div>

                                    {/* TAB CONTENT: VERIFY */}
                                    {activeTab === "verify" && (
                                        <div className="p-8 grid grid-cols-1 lg:grid-cols-5 gap-8">

                                            {/* LEFT COLUMN: Data Peserta (Spans 3 cols) */}
                                            <div className="lg:col-span-3 flex flex-col gap-6">
                                                <div className="flex items-center justify-between">
                                                    <div>
                                                        <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-1.5">Status Verifikasi Terkini</p>
                                                        {getStatusBadge(detailData.verification_status)}
                                                    </div>
                                                    <div className="text-right">
                                                        <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-1.5">Kategori Pendaftar</p>
                                                        <div className="flex flex-col items-end gap-1.5">
                                                            <span className="px-3 py-1 bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300 rounded-lg text-xs font-bold uppercase">{detailData.participant_type?.replace('_', ' ')}</span>
                                                            <span className="text-xs font-bold text-slate-500">{formatCurrency(detailData.amount, detailData.currency)}</span>
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="bg-slate-50 dark:bg-slate-800/30 p-6 rounded-2xl space-y-5 border border-slate-100 dark:border-slate-800">
                                                    <div>
                                                        <p className="text-xs font-bold text-slate-400 mb-1.5 flex items-center gap-1.5"><User className="w-4 h-4" /> Nama Lengkap</p>
                                                        <p className="font-black text-lg text-slate-900 dark:text-white">{detailData.participant_name}</p>
                                                    </div>
                                                    <div className="grid grid-cols-2 gap-6">
                                                        <div>
                                                            <p className="text-xs font-bold text-slate-400 mb-1.5 flex items-center gap-1.5"><Building2 className="w-4 h-4" /> Institusi Asal</p>
                                                            <p className="font-bold text-slate-900 dark:text-white text-sm">{detailData.institution_name || "-"}</p>
                                                        </div>
                                                        <div>
                                                            <p className="text-xs font-bold text-slate-400 mb-1.5 flex items-center gap-1.5"><FileText className="w-4 h-4" /> NIK / NIM</p>
                                                            <p className="font-bold text-slate-900 dark:text-white text-sm font-mono">{detailData.nim || detailData.nik || "-"}</p>
                                                        </div>
                                                    </div>
                                                    <div>
                                                        <p className="text-xs font-bold text-slate-400 mb-1.5 flex items-center gap-1.5"><Phone className="w-4 h-4" /> Kontak Terdaftar</p>
                                                        <div className="flex flex-col gap-1 text-sm font-bold text-slate-900 dark:text-white">
                                                            <span>Email: {detailData.email}</span>
                                                            <span>WA: {detailData.whatsapp_number}</span>
                                                        </div>
                                                    </div>
                                                </div>

                                                {detailData.is_presenter && detailData.submission_title && (
                                                    <div className="bg-blue-50 dark:bg-blue-900/10 p-6 rounded-2xl border border-blue-100 dark:border-blue-900/30">
                                                        <div className="flex items-center justify-between gap-2 mb-2.5">
                                                            <p className="text-xs font-bold text-blue-500 flex items-center gap-1.5"><BookOpen className="w-4 h-4" /> Informasi Karya Tulis (Paper)</p>
                                                            <Button
                                                                type="button"
                                                                size="sm"
                                                                variant="outline"
                                                                disabled={syncPaperMutation.isPending}
                                                                onClick={() => syncPaperMutation.mutate()}
                                                                title="Ambil ulang judul & penulis terbaru dari OJS (jika ada revisi setelah pendaftaran)"
                                                                className="h-7 px-2.5 text-[11px] font-bold text-blue-600 border-blue-200 bg-white hover:bg-blue-50 dark:bg-slate-800 dark:border-blue-900/50 dark:text-blue-400 dark:hover:bg-blue-900/20"
                                                            >
                                                                {syncPaperMutation.isPending ? (
                                                                    <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
                                                                ) : (
                                                                    <RefreshCw className="w-3.5 h-3.5 mr-1" />
                                                                )}
                                                                Sync Ulang Paper
                                                            </Button>
                                                        </div>
                                                        <p className="font-bold text-base text-slate-900 dark:text-white leading-relaxed">{detailData.submission_title}</p>
                                                        <p className="text-sm font-medium text-slate-600 dark:text-slate-400 mt-2">Penulis: <span className="text-slate-900 dark:text-slate-200">{detailData.submission_authors}</span></p>
                                                    </div>
                                                )}

                                                <div className="bg-white dark:bg-slate-900 p-5 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm mt-auto">
                                                    <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-3">Dokumen Lampiran Identitas</p>
                                                    {detailData.ktm_path ? (
                                                        <a href={detailData.ktm_path} target="_blank" rel="noreferrer" className="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 hover:border-blue-500 transition-all group">
                                                            <div className="flex items-center gap-3">
                                                                <div className="p-3 bg-blue-100 text-blue-600 dark:bg-blue-900/50 dark:text-blue-400 rounded-xl group-hover:scale-110 transition-transform">
                                                                    <FileText className="w-5 h-5" />
                                                                </div>
                                                                <div>
                                                                    <p className="font-bold text-sm text-slate-900 dark:text-white">Kartu Tanda Mahasiswa (KTM)</p>
                                                                    <p className="text-xs text-blue-600 dark:text-blue-400 mt-0.5 font-medium group-hover:underline">Buka File Dokumen &rarr;</p>
                                                                </div>
                                                            </div>
                                                        </a>
                                                    ) : (
                                                        <div className="p-4 bg-orange-50 dark:bg-orange-900/10 border border-orange-200 dark:border-orange-900/50 rounded-xl text-orange-700 dark:text-orange-400 text-sm flex items-center gap-3">
                                                            <XCircle className="w-5 h-5 shrink-0 opacity-70" />
                                                            <span className="font-medium">Peserta tidak mengunggah file identitas (KTM).</span>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>

                                            {/* RIGHT COLUMN: Aksi & Pembayaran (Spans 2 cols) */}
                                            <div className="lg:col-span-2 flex flex-col gap-6 relative">

                                                <div className="bg-white dark:bg-slate-900 p-5 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm">
                                                    <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-4">Pembayaran & Tagihan</p>
                                                    <div className="space-y-4">
                                                        <div className="flex items-center justify-between pb-4 border-b border-slate-100 dark:border-slate-800">
                                                            <span className="text-xs font-bold text-slate-500 flex items-center gap-1.5"><CreditCard className="w-4 h-4" /> Status Bayar</span>
                                                            {getPaymentStatusBadge(detailData.status)}
                                                        </div>

                                                        <div className="flex flex-col gap-2">
                                                            <a href={detailData.billing_url} target="_blank" rel="noreferrer" className="w-full flex items-center justify-center gap-2 py-2.5 bg-blue-50 text-blue-700 hover:bg-blue-100 dark:bg-blue-900/30 dark:text-blue-400 dark:hover:bg-blue-900/50 rounded-xl text-sm font-bold transition-colors">
                                                                <Receipt className="w-4 h-4" /> Buka Halaman Invoice
                                                            </a>

                                                            {/* ✅ TOMBOL KIRIM ULANG EMAIL PEMBAYARAN */}
                                                            <Button
                                                                variant="outline"
                                                                className="w-full rounded-xl border-slate-200 text-slate-600 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-300 text-sm font-bold h-auto py-2.5"
                                                                onClick={() => resendPaymentEmailMutation.mutate()}
                                                                disabled={resendPaymentEmailMutation.isPending}
                                                            >
                                                                {resendPaymentEmailMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
                                                                Kirim Ulang Email Pembayaran
                                                            </Button>
                                                        </div>

                                                        {/* LoA Invitation (Hanya untuk Presenter) */}
                                                        {detailData.is_presenter && detailData.invitation_url && (
                                                            <>
                                                                <div className="h-px bg-slate-100 dark:bg-slate-800 w-full my-3"></div>
                                                                <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-3">Letter of Acceptance (LoA)</p>

                                                                {detailData.inv_sent_at && (
                                                                    <div className="mb-3 px-3 py-2 bg-slate-50 dark:bg-slate-800 rounded-lg flex items-start gap-2 border border-slate-100 dark:border-slate-700">
                                                                        <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" />
                                                                        <p className="text-[11px] text-slate-600 dark:text-slate-400">
                                                                            Terkirim pada: <br /><strong className="text-slate-800 dark:text-slate-200">{formatDate(detailData.inv_sent_at)}</strong>
                                                                        </p>
                                                                    </div>
                                                                )}

                                                                <div className="flex flex-col sm:flex-row gap-2">
                                                                    <a href={detailData.invitation_url} target="_blank" rel="noreferrer" className="flex-1 flex items-center justify-center gap-2 py-2.5 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 dark:bg-emerald-900/30 dark:text-emerald-400 dark:hover:bg-emerald-900/50 rounded-xl text-xs font-bold transition-colors">
                                                                        <FileText className="w-4 h-4" /> Lihat PDF
                                                                    </a>
                                                                    <Button
                                                                        variant="outline"
                                                                        className={cn(
                                                                            "flex-1 rounded-xl border-slate-200 text-xs font-bold h-auto py-2.5",
                                                                            detailData.inv_sent_at ? "text-amber-600 hover:bg-amber-50 dark:border-slate-700 dark:text-amber-500" : "text-slate-600 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-300"
                                                                        )}
                                                                        onClick={() => sendInvitationMutation.mutate()}
                                                                        disabled={sendInvitationMutation.isPending}
                                                                    >
                                                                        {sendInvitationMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
                                                                        {detailData.inv_sent_at ? "Kirim Ulang" : "Email LoA"}
                                                                    </Button>
                                                                </div>
                                                            </>
                                                        )}
                                                    </div>
                                                </div>

                                                {/* Form Aksi Verifikasi */}
                                                {detailData.verification_status === "not_required" ? (
                                                    <div className="bg-slate-100 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 p-5 rounded-2xl flex items-start gap-3 mt-auto">
                                                        <ShieldCheck className="w-5 h-5 text-slate-500 shrink-0 mt-0.5" />
                                                        <div>
                                                            <h4 className="text-sm font-bold text-slate-800 dark:text-slate-300">Auto-Verified</h4>
                                                            <p className="text-xs text-slate-500 mt-1 leading-relaxed">
                                                                Kategori ini tidak memerlukan verifikasi manual dari operator.
                                                            </p>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <div className="bg-slate-50 dark:bg-slate-800/30 p-5 rounded-2xl border border-slate-200 dark:border-slate-700 mt-auto flex-1 flex flex-col">
                                                        <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-3">Keputusan Verifikasi</p>

                                                        <div className="grid grid-cols-2 gap-3 mb-4">
                                                            <button
                                                                onClick={() => setVerifyForm({ ...verifyForm, status: "approved" })}
                                                                className={cn("py-3 rounded-xl border-2 font-bold text-sm transition-all flex items-center justify-center gap-2", verifyForm.status === "approved" ? "border-emerald-500 bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400" : "border-slate-200 bg-white text-slate-500 dark:border-slate-700 dark:bg-slate-900")}
                                                            >
                                                                <CheckCircle2 className="w-4 h-4" /> Setujui
                                                            </button>
                                                            <button
                                                                onClick={() => setVerifyForm({ ...verifyForm, status: "rejected" })}
                                                                className={cn("py-3 rounded-xl border-2 font-bold text-sm transition-all flex items-center justify-center gap-2", verifyForm.status === "rejected" ? "border-rose-500 bg-rose-50 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400" : "border-slate-200 bg-white text-slate-500 dark:border-slate-700 dark:bg-slate-900")}
                                                            >
                                                                <XCircle className="w-4 h-4" /> Tolak
                                                            </button>
                                                        </div>

                                                        <div className="space-y-2 mb-5 flex-1 flex flex-col">
                                                            <label className="text-xs font-bold text-slate-500">Catatan Revisi / Alasan</label>
                                                            <textarea
                                                                placeholder={verifyForm.status === "rejected" ? "Wajib diisi jika ditolak (cth: KTM buram, tidak terbaca)..." : "Opsional: Catatan untuk pendaftar..."}
                                                                value={verifyForm.reason}
                                                                onChange={(e) => setVerifyForm({ ...verifyForm, reason: e.target.value })}
                                                                className="w-full flex-1 min-h-[80px] p-3 bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-600 rounded-xl text-sm outline-none focus:ring-2 focus:ring-blue-500 resize-none transition-colors"
                                                            />
                                                        </div>

                                                        <Button
                                                            className="w-full rounded-xl bg-blue-600 hover:bg-blue-700 text-white h-12 font-bold shadow-md"
                                                            onClick={() => verifyMutation.mutate()}
                                                            disabled={verifyMutation.isPending || (verifyForm.status === "rejected" && !verifyForm.reason)}
                                                        >
                                                            {verifyMutation.isPending ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : <ShieldCheck className="w-5 h-5 mr-2" />}
                                                            Simpan Keputusan
                                                        </Button>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    )}

                                    {/* TAB CONTENT: EDIT DATA */}
                                    {activeTab === "edit" && (
                                        <div className="p-8 max-w-4xl mx-auto w-full animate-in fade-in duration-300">
                                            <div className="bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-900/50 p-4 sm:p-5 rounded-2xl mb-8 flex gap-3 sm:gap-4 text-sm text-amber-800 dark:text-amber-400 shadow-sm">
                                                <div className="p-2 bg-amber-100 dark:bg-amber-900/40 rounded-full h-fit shrink-0">
                                                    <Edit3 className="w-5 h-5" />
                                                </div>
                                                <p className="font-medium leading-relaxed mt-1">
                                                    Gunakan form ini untuk memperbaiki kesalahan ketik atau mengganti lampiran KTM peserta secara sepihak <strong>tanpa harus membatalkan tagihan.</strong>
                                                </p>
                                            </div>

                                            {/* Form Grid Responsif */}
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6 mb-8">
                                                <div className="space-y-2">
                                                    <label className="text-[11px] font-bold text-slate-500 uppercase tracking-widest">Nama Lengkap</label>
                                                    <input type="text" value={editForm.participant_name} onChange={(e) => setEditForm({ ...editForm, participant_name: e.target.value })} className="w-full p-3.5 bg-slate-50 dark:bg-slate-800/50 border border-slate-300 dark:border-slate-700 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:bg-white dark:focus:bg-slate-900 outline-none transition-all" />
                                                </div>
                                                <div className="space-y-2">
                                                    <label className="text-[11px] font-bold text-slate-500 uppercase tracking-widest">NIM / NIK</label>
                                                    <input type="text" value={editForm.nim} onChange={(e) => setEditForm({ ...editForm, nim: e.target.value })} className="w-full p-3.5 bg-slate-50 dark:bg-slate-800/50 border border-slate-300 dark:border-slate-700 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:bg-white dark:focus:bg-slate-900 outline-none font-mono transition-all" />
                                                </div>
                                                <div className="space-y-2">
                                                    <label className="text-[11px] font-bold text-slate-500 uppercase tracking-widest">Alamat Email</label>
                                                    <input type="email" value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} className="w-full p-3.5 bg-slate-50 dark:bg-slate-800/50 border border-slate-300 dark:border-slate-700 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:bg-white dark:focus:bg-slate-900 outline-none transition-all" />
                                                </div>
                                                <div className="space-y-2">
                                                    <label className="text-[11px] font-bold text-slate-500 uppercase tracking-widest">Nomor WhatsApp</label>
                                                    <input type="text" value={editForm.whatsapp_number} onChange={(e) => setEditForm({ ...editForm, whatsapp_number: e.target.value })} className="w-full p-3.5 bg-slate-50 dark:bg-slate-800/50 border border-slate-300 dark:border-slate-700 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:bg-white dark:focus:bg-slate-900 outline-none transition-all" />
                                                </div>
                                            </div>

                                            {/* File Upload Presisi */}
                                            <div className="mb-10 p-6 bg-slate-50 dark:bg-slate-800/30 border border-slate-200 dark:border-slate-700 rounded-2xl">
                                                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                                                    <div>
                                                        <label className="text-sm font-bold text-slate-900 dark:text-white flex items-center gap-2 mb-1">
                                                            <Upload className="w-4 h-4 text-blue-500" /> Ganti Dokumen KTM (Opsional)
                                                        </label>
                                                        <p className="text-xs text-slate-500">Unggah file PDF/JPG baru untuk merevisi dokumen yang salah.</p>
                                                    </div>
                                                    <div className="flex items-center gap-3 w-full sm:w-auto">
                                                        <label className="flex items-center justify-center px-6 py-2.5 bg-white dark:bg-slate-900 border-2 border-dashed border-slate-300 dark:border-slate-600 rounded-xl cursor-pointer hover:border-blue-500 dark:hover:border-blue-500 transition-colors w-full sm:w-auto shrink-0">
                                                            <span className="text-sm font-bold text-slate-600 dark:text-slate-300 whitespace-nowrap">Pilih File</span>
                                                            <input type="file" className="hidden" accept=".pdf,.jpg,.jpeg,.png" onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                                                if (e.target.files && e.target.files[0]) setEditForm({ ...editForm, ktm_file: e.target.files[0] });
                                                            }} />
                                                        </label>
                                                    </div>
                                                </div>
                                                {editForm.ktm_file && (
                                                    <div className="mt-4 p-3 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-900/50 rounded-xl flex items-center gap-2">
                                                        <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                                                        <span className="text-sm font-bold text-emerald-700 dark:text-emerald-400 truncate">{editForm.ktm_file.name} terpilih</span>
                                                    </div>
                                                )}
                                            </div>

                                            {/* Override Kunci Nama/Email (Khusus Super Admin, muncul setelah percobaan diblokir) */}
                                            {showOverrideField && (
                                                <div className="mb-8 p-6 bg-rose-50 dark:bg-rose-900/10 border border-rose-200 dark:border-rose-900/50 rounded-2xl animate-in fade-in duration-300">
                                                    <p className="text-sm font-bold text-rose-700 dark:text-rose-400 mb-1.5 flex items-center gap-2">
                                                        <ShieldCheck className="w-4 h-4" /> Override Super Admin
                                                    </p>
                                                    <p className="text-xs text-rose-600 dark:text-rose-400/80 mb-3 leading-relaxed">
                                                        Nama/Email tidak bisa diubah karena tagihan VA sudah terbit di sistem Bank. Nomor VA tidak akan berubah - hanya data lokal di KONASPI yang dikoreksi. Wajib isi alasan sebelum menyimpan.
                                                    </p>
                                                    <textarea
                                                        value={overrideReason}
                                                        onChange={(e) => setOverrideReason(e.target.value)}
                                                        placeholder="Contoh: Peserta salah ketik nama saat daftar, sudah dikonfirmasi via WhatsApp tanggal ..."
                                                        rows={3}
                                                        className="w-full p-3.5 bg-white dark:bg-slate-900 border border-rose-300 dark:border-rose-800 rounded-xl text-sm focus:ring-2 focus:ring-rose-500 outline-none transition-all resize-none"
                                                    />
                                                </div>
                                            )}

                                            {/* Action Buttons */}
                                            <div className="flex flex-col-reverse sm:flex-row justify-end gap-3 pt-6 border-t border-slate-100 dark:border-slate-800">
                                                <Button variant="ghost" onClick={() => setActiveTab("verify")} className="rounded-xl h-12 px-6">Batal</Button>
                                                <Button
                                                    onClick={() => updateMutation.mutate()}
                                                    disabled={updateMutation.isPending || (showOverrideField && !overrideReason.trim())}
                                                    className="rounded-xl bg-amber-500 hover:bg-amber-600 text-white font-bold h-12 px-8 shadow-sm transition-all"
                                                >
                                                    {updateMutation.isPending ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : <CheckCircle2 className="w-5 h-5 mr-2" />}
                                                    {showOverrideField ? "Simpan dengan Override" : "Simpan Perubahan Data"}
                                                </Button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* ================================================================================= */}
            {/* ✅ MODAL EXPORT DATA REGISTRASI & VERIFIKASI (Z-Index Aman z-[999]) */}
            {/* ================================================================================= */}
            {isExportModalOpen && (
                <div className="fixed inset-0 z-[999] flex items-center justify-center p-4 md:p-6 bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-200">
                    <div className="bg-white dark:bg-slate-900 rounded-[2rem] w-full max-w-lg flex flex-col shadow-2xl border border-slate-200 dark:border-slate-700 overflow-hidden animate-in zoom-in-95">
                        <div className="px-8 py-5 border-b border-slate-100 dark:border-slate-800 flex justify-between items-center bg-slate-50 dark:bg-slate-800/50 shrink-0">
                            <div>
                                <h3 className="font-black text-xl text-slate-900 dark:text-white flex items-center gap-2">
                                    <Download className="w-6 h-6 text-emerald-600" /> Export Excel
                                </h3>
                                <p className="text-xs text-slate-500 font-medium mt-1">Unduh data verifikasi & pembayaran pendaftar.</p>
                            </div>
                            <button onClick={() => !isExporting && setIsExportModalOpen(false)} disabled={isExporting} className="p-2.5 bg-slate-200/50 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 rounded-full transition-colors disabled:opacity-50">
                                <X className="w-5 h-5 text-slate-500 dark:text-slate-400" />
                            </button>
                        </div>
                        
                        <div className="p-6 md:p-8 space-y-4">
                            {/* Pilihan Conference */}
                            <div className="space-y-1.5">
                                <label className="text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider">Conference Track</label>
                                <select
                                    value={exportConferenceId}
                                    onChange={(e) => setExportConferenceId(e.target.value)}
                                    className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl px-4 py-2.5 text-sm font-medium focus:ring-2 focus:ring-blue-500 outline-none transition-all"
                                >
                                    <option value="all">Semua Conference</option>
                                    {conferencesData?.map(conf => (
                                        <option key={conf.conference_id} value={conf.conference_id.toString()}>
                                            {conf.slug.toUpperCase()} - {conf.name}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {/* Pilihan Role */}
                            <div className="space-y-1.5">
                                <label className="text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider">Role Pendaftar</label>
                                <select
                                    value={exportPresenter}
                                    onChange={(e) => setExportPresenter(e.target.value)}
                                    className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl px-4 py-2.5 text-sm font-medium focus:ring-2 focus:ring-blue-500 outline-none transition-all"
                                >
                                    <option value="all">Semua Role</option>
                                    <option value="1">Presenter (Pemakalah)</option>
                                    <option value="0">Non-Presenter (Peserta)</option>
                                </select>
                            </div>

                            {/* Pilihan Status Pembayaran */}
                            <div className="space-y-1.5">
                                <label className="text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider">Status Pembayaran</label>
                                <select
                                    value={exportPaymentStatus}
                                    onChange={(e) => setExportPaymentStatus(e.target.value)}
                                    className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl px-4 py-2.5 text-sm font-medium focus:ring-2 focus:ring-blue-500 outline-none transition-all"
                                >
                                    <option value="all">Semua Pembayaran</option>
                                    <option value="paid">Lunas (Paid)</option>
                                    <option value="unpaid">Belum Bayar (Unpaid)</option>
                                    <option value="expired">Kedaluwarsa (Expired)</option>
                                    <option value="refunded">Dikembalikan (Refunded)</option>
                                </select>
                            </div>

                            {/* Pilihan Status Verifikasi */}
                            <div className="space-y-1.5">
                                <label className="text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider">Status Verifikasi</label>
                                <select
                                    value={exportVerificationStatus}
                                    onChange={(e) => setExportVerificationStatus(e.target.value)}
                                    className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl px-4 py-2.5 text-sm font-medium focus:ring-2 focus:ring-blue-500 outline-none transition-all"
                                >
                                    <option value="all">Semua Status Verifikasi</option>
                                    <option value="pending">Pending</option>
                                    <option value="approved">Approved</option>
                                    <option value="rejected">Rejected</option>
                                    <option value="not_required">Auto-Verify</option>
                                </select>
                            </div>
                        </div>

                        <div className="px-6 py-4 md:px-8 md:py-5 border-t border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/50 flex justify-end gap-3 shrink-0">
                            <Button variant="ghost" onClick={() => setIsExportModalOpen(false)} disabled={isExporting} className="rounded-xl h-11 px-6">Batal</Button>
                            <Button 
                                onClick={handleExportExcel} 
                                disabled={isExporting} 
                                className="rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold h-11 px-6 shadow-sm transition-all"
                            >
                                {isExporting ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : <Download className="w-5 h-5 mr-2" />} 
                                {isExporting ? "Mengunduh..." : "Export Excel"}
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            <BypassRegistrationModal 
                isOpen={isBypassModalOpen} 
                onClose={() => setIsBypassModalOpen(false)}
                conferences={conferencesData || []} 
            />
        </div>
    );
}